<template>
  <span>
    <v-btn flat color="primary" :loading="downloading" @click="downloadTemplate()">
      <v-icon left>cloud_download</v-icon>Import template
    </v-btn>
    <v-btn color="primary" @click="open()">
      <v-icon left>cloud_upload</v-icon>Import customers
    </v-btn>

    <v-dialog v-model="dialog" max-width="1000" persistent>
      <v-card>
        <v-card-title>
          <h3>Import customers into {{ networkName }}</h3>
        </v-card-title>
        <v-card-text>
          <p>
            Use the import template for this network (one row per service; a row with only a unit number creates the unit).
            The file is checked first; nothing is saved until you click Import. No ISP emails or network jobs are sent.
          </p>
          <v-layout row wrap align-center>
            <v-flex xs12 sm7>
              <input ref="file" type="file" accept=".csv,text/csv" @change="fileChosen" />
              <div v-if="fileName" class="grey--text">{{ fileName }}</div>
            </v-flex>
            <v-flex xs12 sm5>
              <v-text-field v-model="billingStart" type="date" label="Billing starts from"
                hint="Accounts activated earlier are billed from this date" persistent-hint></v-text-field>
            </v-flex>
          </v-layout>

          <v-alert :value="!!message" :type="messageType" outline>{{ message }}</v-alert>

          <div v-if="result">
            <p class="mt-3">
              <strong>{{ result.summary.rows }}</strong> rows:
              {{ result.summary.newUnits }} new units, {{ result.summary.accounts }} service accounts,
              {{ result.summary.onts }} ONTs<span v-if="result.summary.withoutOnt">, {{ result.summary.withoutOnt }} accounts without an ONT (not billed until one is linked)</span>.
              <span v-if="result.summary.errors" class="red--text"><strong>{{ result.summary.errors }} rows have errors.</strong></span>
              <span v-if="result.unknownColumns && result.unknownColumns.length" class="orange--text">
                Ignored columns: {{ result.unknownColumns.join(', ') }}.
              </span>
            </p>
            <v-data-table :headers="headers" :items="result.rows" :rows-per-page-items="[25, 100, 500]" class="elevation-1">
              <template v-slot:items="props">
                <td>{{ props.item.line }}</td>
                <td>{{ props.item.unit }}</td>
                <td>{{ props.item.action }}</td>
                <td>
                  <div v-for="(e, i) in props.item.errors" :key="'e' + i" class="red--text">{{ e }}</div>
                  <div v-for="(n, i) in props.item.notes" :key="'n' + i" class="grey--text">{{ n }}</div>
                </td>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="close()">Close</v-btn>
          <v-btn flat color="primary" :disabled="!csv || busy || done" :loading="busy && !committing" @click="send(false)">Check file</v-btn>
          <v-btn color="primary" :disabled="!canImport" :loading="committing" @click="send(true)">Import</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import Api from "@/services/Api";

function firstOfNextMonth() {
  const d = new Date();
  const n = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  return n.getFullYear() + "-" + String(n.getMonth() + 1).padStart(2, "0") + "-01";
}

export default {
  props: ["locationNetworkId", "networkName"],
  data() {
    return {
      dialog: false,
      downloading: false,
      csv: null,
      fileName: "",
      billingStart: firstOfNextMonth(),
      result: null,
      checked: false,
      busy: false,
      committing: false,
      done: false,
      message: "",
      messageType: "info",
      headers: [
        { text: "Line", value: "line", sortable: false },
        { text: "Unit", value: "unit", sortable: false },
        { text: "Will create", value: "action", sortable: false },
        { text: "Errors / notes", value: "errors", sortable: false }
      ]
    };
  },
  computed: {
    canImport() {
      return this.checked && !this.busy && !this.done && this.result && this.result.summary.errors === 0;
    }
  },
  methods: {
    open() {
      this.reset();
      this.dialog = true;
    },
    close() {
      this.dialog = false;
      if (this.done) this.$emit("imported");
    },
    reset() {
      this.csv = null;
      this.fileName = "";
      this.result = null;
      this.checked = false;
      this.done = false;
      this.message = "";
      if (this.$refs.file) this.$refs.file.value = "";
    },
    fileChosen(e) {
      const file = e.target.files && e.target.files[0];
      this.result = null;
      this.checked = false;
      this.done = false;
      this.message = "";
      if (!file) return;
      this.fileName = file.name;
      const reader = new FileReader();
      reader.onload = () => {
        this.csv = reader.result;
      };
      reader.readAsText(file);
      // Clear the input so choosing the same (corrected) file again still triggers a change.
      e.target.value = "";
    },
    async downloadTemplate() {
      this.downloading = true;
      try {
        const response = await Api().get("/customerImport/template/" + this.locationNetworkId, { responseType: "text" });
        const link = document.createElement("a");
        link.href = "data:text/csv;charset=utf-8," + encodeURIComponent(response.data);
        link.download = "customer_import_" + (this.networkName || this.locationNetworkId).replace(/[^A-Za-z0-9]+/g, "_") + ".csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (e) {
        alert((e.response && e.response.data && e.response.data.message) || "Could not download the template.");
      }
      this.downloading = false;
    },
    async send(commit) {
      if (commit && !confirm("Import " + this.result.summary.rows + " rows into " + this.networkName + "?")) return;
      this.busy = true;
      this.committing = commit;
      this.message = "";
      try {
        const response = await Api().post("/customerImport/" + this.locationNetworkId, {
          csv: this.csv,
          billingStart: this.billingStart,
          commit
        });
        this.result = response.data;
        if (commit) {
          const c = response.data.created;
          this.done = true;
          this.messageType = "success";
          this.message = "Imported: " + c.units + " units, " + c.contacts + " new customers, " + c.onts + " ONTs, " + c.accounts + " service accounts.";
        } else {
          this.checked = true;
          this.messageType = response.data.summary.errors ? "error" : "success";
          this.message = response.data.summary.errors
            ? "Fix the rows marked in red and choose the file again."
            : "No errors. Check the list, then click Import.";
        }
      } catch (e) {
        const data = (e.response && e.response.data) || {};
        if (data.rows) this.result = { summary: data.summary, rows: data.rows };
        this.messageType = "error";
        this.message = data.message || "The request failed.";
      }
      this.busy = false;
      this.committing = false;
    }
  }
};
</script>
